import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MiqMoment } from '@miq/utils';
import { NUMPAD_PERIOD, PERIOD, DASH, NUMPAD_MINUS } from '@angular/cdk/keycodes';
import { IOption } from '@shared/models/icommons.model';
import { DataType, InputType } from '@miq/catalog';
import { CbreAlertService } from '@cbre/alerts';
import moment from 'moment';
import { InputProps } from '../filters-bar/input-props';
import { DateRangeFilterOptions } from '@records/records-search/filter-set/filter-option/filter-option.const';
import { SelectionChange } from '../inputs/year-picker/year-picker.component';
import { validateYearRange } from '../inputs/year-picker/year-validation.util';

@Component({
  selector: 'miq-range-item',
  templateUrl: './range-item.component.html',
  styleUrls: ['./range-item.component.scss'],
  standalone: false,
})
export class RangeItemComponent implements OnInit, OnDestroy {
  @Input() filter: IOption;
  @Input() aggregation: any;
  @Input() form: UntypedFormGroup;
  @Input() isLandingPage: any;
  @Input() isReport = false;
  @Output() public removeFilter = new EventEmitter();
  @Output() public rangeChange = new EventEmitter();
  minDate: string | moment.Moment;
  maxDate: string | moment.Moment;
  displayFromClearButton: boolean;
  displayToClearButton: boolean;
  rangeFormGroup: UntypedFormGroup;
  restrictedKeyCodes = [];
  allowedKeyCodes = [];

  constructor(
    private fb: UntypedFormBuilder,
    private cbreAlertService: CbreAlertService
  ) {}

  ngOnInit(): void {
    this.filter.value = !!this.filter.label ? this.filter.label : this.filter.value;
    this.filter.key = !!this.filter.key ? this.filter.key : this.filter.field;
    const fromValue = this.getValueByDataType(this.fromControl.value);
    const toValue = this.getValueByDataType(this.toControl.value);
    const inputPropsFilter = this.filter as InputProps;
    /**
     * The default behavior for inputs should be to update on blur event
     *  to avoid making a request on each character entered by the user
     */
    this.rangeFormGroup = this.fb.group({
      from: [fromValue, inputPropsFilter.controlOptions || { updateOn: 'blur' }],
      to: [toValue, inputPropsFilter.controlOptions || { updateOn: 'blur' }],
    });
    this.rangeFormGroup.valueChanges.subscribe(({ from, to }) => {
      this.globalRangeGroup.patchValue({
        from: this.formatDateValue(from, 'from'),
        to: this.formatDateValue(to, 'to'),
      });
      this.rangeChange.emit({ [this.filter.field]: this.globalRangeGroup.value });
    });
    this.globalRangeGroup.valueChanges.subscribe(({ from, to }) => {
      if (from == null && to == null) {
        this.rangeFormGroup.controls['from'].setValue(null, { emitEvent: false });
        this.rangeFormGroup.controls['to'].setValue(null, { emitEvent: false });
        this.displayFromClearButton = false;
        this.displayToClearButton = false;
        this.minDate = null;
        this.maxDate = null;
        this.rangeChange.emit({ [this.filter.field]: this.globalRangeGroup.value });
      } else if (!this.filter.allowNegativeNumbers) {
        const fromVal = this.getValueByDataType(from);
        const toVal = this.getValueByDataType(to);
        this.rangeFormGroup.controls['from'].setValue(fromVal, { emitEvent: false });
        this.rangeFormGroup.controls['to'].setValue(toVal, { emitEvent: false });
        this.displayFromClearButton = !!fromVal;
        this.displayToClearButton = !!toVal;
      }
    });
    this.displayFromClearButton = Boolean(this.fromControl.value);
    this.displayToClearButton = Boolean(this.toControl.value);

    // Add restricted key codes for filters that should only allow numbers
    this.restrictedKeyCodes =
      this.filter.dataType === DataType.Integer && this.filter.inputType === InputType.Number.toLowerCase()
        ? [NUMPAD_PERIOD, PERIOD]
        : [];
    this.allowedKeyCodes = this.filter.allowNegativeNumbers ? [DASH, NUMPAD_MINUS] : [];

    this.addEnumCtrlForDates();

    this.setInitialValuesForDates();
  }

  private getValueByDataType(value: any) {
    let result = value;
    if (this.isDataTypeDate()) {
      const dateValue = moment(value, MiqMoment.utcInputFormat).utc();
      result = dateValue.isValid() ? dateValue : null;
    }
    return result;
  }

  dateEvent(type: 'from' | 'to', matDatepickerInputEvent: MatDatepickerInputEvent<moment.Moment>) {
    if (type === 'from') {
      this.minDate = matDatepickerInputEvent.value;
      this.displayFromClearButton = true;
    } else {
      this.maxDate = matDatepickerInputEvent.value;
      this.displayToClearButton = true;
    }

    // If a date is changed manually by the user it is necessary to clean the selected period
    this.updateEnumSelectionControl(null);
  }

  clearDateFilter(filterType: 'from' | 'to', emitEvent = true) {
    this.rangeFormGroup.controls[filterType].reset(null, { emitEvent });
    if (filterType === 'from') {
      this.displayFromClearButton = false;
      this.minDate = null;
      return;
    }
    this.displayToClearButton = false;
    this.maxDate = null;
  }

  private isDataTypeDate(): boolean {
    return (
      this.filter.dataType === 'timestamp with time zone' ||
      this.filter.dataType === 'date' ||
      this.filter.inputType === 'date'
    );
  }

  private formatDateValue(controlValue: string | moment.Moment, filterType: 'from' | 'to') {
    if (controlValue && this.isDataTypeDate()) {
      const momentTransformingMethod = filterType === 'from' ? 'startOf' : 'endOf';
      const date = moment(controlValue, MiqMoment.utcInputFormat).utc();
      controlValue = date[momentTransformingMethod]('day').format();
    }
    return controlValue;
  }

  get globalRangeGroup(): UntypedFormGroup {
    return (this.form.controls[this.filter.key] || this.form.controls[this.filter.field]) as UntypedFormGroup;
  }

  get fromControl(): UntypedFormControl {
    return this.globalRangeGroup.controls['from'] as UntypedFormControl;
  }

  get toControl(): UntypedFormControl {
    return this.globalRangeGroup.controls['to'] as UntypedFormControl;
  }

  get enumSelectionControl(): UntypedFormControl {
    return (this.parentGroupControl as UntypedFormGroup).controls['enumSelection'] as UntypedFormControl;
  }

  private updateEnumSelectionControl(value: null | DateRangeFilterOptions) {
    if (this.enumSelectionControl) {
      this.enumSelectionControl.setValue(value);
    }
  }

  ngOnDestroy() {
    /** label translation purpose added */
  }

  public updateForm(event, ctrlName: 'from' | 'to'): void {
    const { value } = event.target;
    if (ctrlName === 'from') {
      if (Number.isInteger(this.toControl.value) && value >= this.toControl.value) {
        return this.handleRangeError(false);
      }
      this.fromControl.patchValue(value);
    } else if (ctrlName === 'to') {
      const minValue = this.fromControl.value ?? 0;
      if (minValue >= value) {
        return this.handleRangeError(true);
      }
      this.toControl.patchValue(value);
    } else {
      throw new Error('No valid name control range');
    }
  }

  public handleRangeError(shouldBeGreater: boolean): void {
    const errorMessage = shouldBeGreater
      ? 'detailsErrors.error.sections.maxValueLessThanMin'
      : 'detailsErrors.error.sections.minValueGreaterThanMax';

    this.cbreAlertService.showErrorSnackBar_l10n(errorMessage);
  }

  private addEnumCtrlForDates() {
    if (this.filter.dataType === 'date') {
      (this.parentGroupControl as UntypedFormGroup).addControl('enumSelection', new UntypedFormControl(), {
        emitEvent: false,
      });
    }
  }

  get parentGroupControl(): UntypedFormGroup | UntypedFormControl {
    return (this.form.controls[this.filter.key] || this.form.controls[this.filter.field]) as
      | UntypedFormGroup
      | UntypedFormControl;
  }

  private setInitialValuesForDates() {
    // Set the min and max date for the date and year selector
    if (this.filter.inputType === InputType.Date.toLowerCase()) {
      this.minDate = this.fromControl.value;
      this.maxDate = this.toControl.value;
    } else {
      this.minDate = this.fromControl.value
        ? moment()
            .day(1)
            .month(0)
            .year(Number(this.fromControl.value) + 1)
        : null;
      this.maxDate = this.toControl.value ? moment().day(1).month(0).year(this.toControl.value) : null;
    }
  }

  onYearSelected(evt: SelectionChange) {
    const { id, year, date } = evt;
    let fromYear = this.fromControl.value;
    if (id === 'from') {
      fromYear = typeof year === 'number' ? year : null;
    }
    const toYear = id === 'to' ? (typeof year === 'number' ? year : null) : this.toControl.value;

    const isValidRange = validateYearRange(fromYear, toYear);
    if (!isValidRange) {
      if (id === 'to') {
        this.rangeFormGroup.get('to').setValue(null);
        this.maxDate = null;
      } else if (id === 'from') {
        this.rangeFormGroup.get('from').setValue(null);
        this.minDate = null;
      }
      this.rangeChange.emit({ [this.filter.field]: this.globalRangeGroup.value });
      return;
    }

    if (id === 'from') {
      const selectedFromYear = typeof year === 'number' ? year : moment(date).year();
      this.minDate = moment()
        .day(1)
        .month(0)
        .year(selectedFromYear + 1);
      this.displayFromClearButton = true;
    } else {
      this.maxDate = date;
      this.displayToClearButton = true;
    }
    this.rangeFormGroup.get(id).setValue(year);
  }
}
